import { useState } from "react";
import { Link } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Building2, GraduationCap, CheckCircle2, Loader2 } from "lucide-react";
import api from "../../api/api";
import VerifyCodeForm from "./VerifyCodeForm";

const TYPES = [
    { key: "education", label: "Education", icon: GraduationCap,
      //old blurb: "Colleges and universities. Run programs for your students for free. Includes 25 counselor licenses and 25 programs." },
      // License counts are set by Build My Biz Brand after talking with the org
      blurb: "Colleges and universities. Run programs for your students. Build My Biz Brand sets up your licenses with you." },
    { key: "enterprise", label: "Enterprise", icon: Building2,
      //old blurb: "Companies and workforce programs. Your coaches can view the progress students choose to share. Starts with 1 counselor license and 1 program." },
      blurb: "Companies and workforce programs. Your coaches can view the progress students choose to share. Build My Biz Brand sets up your licenses with you." },
];

const inputCls = (bad) => `w-full rounded-xl border bg-white p-3 text-slate-900 transition-colors ${bad ? "border-red-500" : "border-slate-300"}`;

/**
 * Public organization signup (Enterprise Enrollment Flow). Creates a pending
 * organization + an org admin login; the email is verified with a 6-digit code;
 * Build My Biz Brand approves the organization before anyone can sign in.
 */
export default function OrgSignup() {
    const [stage, setStage] = useState("form"); // form | verify | done
    const [form, setForm] = useState({
        orgName: "", orgType: "education", firstName: "", lastName: "", email: "", phone: "", password: "",
        //old coachLicenses: "", programs: "",
        programs: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState("");
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const defaults = form.orgType === "education" ? 25 : 1;

    const validate = () => {
        const e = {};
        if (!form.orgName.trim()) e.orgName = "Enter your organization's name.";
        if (!form.firstName.trim()) e.firstName = "Required.";
        if (!form.lastName.trim()) e.lastName = "Required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email.";
        // Mirrors user_service.validate_password on the backend.
        if (form.password.length < 8 || !/[A-Z]/.test(form.password) || /\s/.test(form.password)) {
            e.password = "At least 8 characters with an uppercase letter, and no spaces.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        setServerError("");
        if (!validate()) return;
        setBusy(true);
        try {
            await api.post("/org/signup", {
                ...form,
                email: form.email.trim().toLowerCase(),
                //old coachLicenses: form.coachLicenses || undefined,
                programs: form.programs || undefined,
            });
            setStage("verify");
        } catch (err) {
            setServerError(err.response?.data?.error || "We couldn't create your organization. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <main className="mx-auto max-w-2xl px-4 py-16">
                <div className="rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-2xl shadow-primary-200/20 sm:p-10">
                    {stage === "form" && (
                        <>
                            <h1 className="mb-2 text-3xl font-extrabold">Set up your organization</h1>
                            <p className="mb-8 text-slate-500">Bring Build My Biz Brand to your students, clients or employees. We'll review your request and get back to you.</p>
                            {serverError && <p role="alert" className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{serverError}</p>}
                            <form onSubmit={submit} className="space-y-5" noValidate>
                                <fieldset>
                                    <legend className="mb-2 block text-sm font-semibold">Organization type</legend>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {TYPES.map(({ key, label, icon: Icon, blurb }) => (
                                            <label key={key} className={`cursor-pointer rounded-2xl border-2 p-4 transition-colors ${form.orgType === key ? "border-primary-500 bg-primary-50" : "border-slate-200 hover:border-slate-300"}`}>
                                                <input type="radio" name="orgType" value={key} checked={form.orgType === key} onChange={set("orgType")} className="sr-only" />
                                                <span className="flex items-center gap-2 font-bold"><Icon size={18} /> {label}</span>
                                                <span className="mt-1 block text-xs text-slate-500">{blurb}</span>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                                <div>
                                    <label htmlFor="os-org" className="mb-1 block text-sm font-semibold">Company or school name</label>
                                    <input id="os-org" className={inputCls(errors.orgName)} value={form.orgName} onChange={set("orgName")} maxLength={200} />
                                    {errors.orgName && <p className="mt-1 text-xs text-red-600">{errors.orgName}</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="os-first" className="mb-1 block text-sm font-semibold">Your first name</label>
                                        <input id="os-first" className={inputCls(errors.firstName)} value={form.firstName} onChange={set("firstName")} maxLength={100} />
                                        {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="os-last" className="mb-1 block text-sm font-semibold">Last name</label>
                                        <input id="os-last" className={inputCls(errors.lastName)} value={form.lastName} onChange={set("lastName")} maxLength={100} />
                                        {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
                                    </div>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="os-email" className="mb-1 block text-sm font-semibold">Work email</label>
                                        <input id="os-email" type="email" autoComplete="email" className={inputCls(errors.email)} value={form.email} onChange={set("email")} maxLength={255} />
                                        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                                        <p className="mt-1 text-xs text-slate-500">Use an email that doesn't already have a Build My Biz Brand account.</p>
                                    </div>
                                    <div>
                                        <label htmlFor="os-phone" className="mb-1 block text-sm font-semibold">Phone (optional)</label>
                                        <input id="os-phone" type="tel" className={inputCls(false)} value={form.phone} onChange={set("phone")} maxLength={50} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="os-password" className="mb-1 block text-sm font-semibold">Password</label>
                                    <div className="relative">
                                        <input id="os-password" type={showPassword ? "text" : "password"} autoComplete="new-password"
                                               className={`${inputCls(errors.password)} pr-11`} value={form.password} onChange={set("password")} />
                                        <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                                            {showPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    {/* old (Build My Biz Brand sets license counts with the org now)
                                    <div>
                                        <label htmlFor="os-coaches" className="mb-1 block text-sm font-semibold">Counselor licenses</label>
                                        <input id="os-coaches" type="number" min={1} max={defaults} placeholder={String(defaults)} className={inputCls(false)} value={form.coachLicenses} onChange={set("coachLicenses")} />
                                    </div>
                                    */}
                                    <div>
                                        <label htmlFor="os-programs" className="mb-1 block text-sm font-semibold">Programs</label>
                                        <input id="os-programs" type="number" min={1} max={defaults} placeholder={String(defaults)} className={inputCls(false)} value={form.programs} onChange={set("programs")} />
                                    </div>
                                    {/* old <p className="col-span-2 -mt-2 text-xs text-slate-500">Up to {defaults} each to start. Need more? We'll set it up when we review your request.</p> */}
                                    <p className="col-span-2 -mt-2 text-xs text-slate-500">Up to {defaults} to start. Need more? We'll set it up when we review your request.</p>
                                </div>
                                <button type="submit" disabled={busy}
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700 disabled:opacity-60">
                                    {busy && <Loader2 size={16} className="animate-spin" />} Create organization
                                </button>
                            </form>
                            <p className="mt-6 text-center text-sm text-slate-600">
                                Signing up for yourself? <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-700">Create a personal account</Link>
                            </p>
                        </>
                    )}

                    {stage === "verify" && (
                        <VerifyCodeForm
                            email={form.email.trim().toLowerCase()}
                            onVerified={() => setStage("done")}
                            verifyEndpoint="/org/signup/verify"
                            resendEndpoint="/org/signup/resend"
                        />
                    )}

                    {stage === "done" && (
                        <div className="text-center">
                            <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-500" />
                            <h1 className="mb-2 text-3xl font-extrabold">Request received</h1>
                            <p className="mb-8 text-slate-500">
                                Thanks! Our team will review {form.orgName} and email you when it's approved.
                                Then sign in with {form.email} to create programs and invite students.
                            </p>
                            <Link to="/" className="font-semibold text-primary-600 hover:text-primary-700">Back to home</Link>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
